/**
 * View-model "sổ giao dịch hợp nhất": gom thu, chi, hoàn tiền, chuyển khoản, trả thẻ,
 * trả nợ từ các collection riêng thành MỘT danh sách để xem/tìm/lọc. Thuần hàm, có test.
 *
 * Quy ước hiển thị `amount`: dấu theo tác động lên TIỀN CỦA NGƯỜI DÙNG —
 * thu và hoàn tiền (+), chi (−), trả thẻ/trả nợ (−, nhưng KHÔNG phải chi tiêu),
 * chuyển khoản giữa ví của chính mình = 0 (chỉ phí chuyển là một khoản chi tự sinh riêng).
 * Tổng "Chi" dùng chiều có dấu chuẩn (hoàn tiền trừ đi) — khớp finance-core.
 */
import { signedAmount } from '../../../finance';

export type EntryKind = 'income' | 'expense' | 'refund' | 'transfer' | 'cardPayment' | 'debtPayment';

export interface LedgerEntry {
  key: string;
  id: string;
  kind: EntryKind;
  date: Date;
  title: string;
  subtitle: string;
  /** Dòng chính khi hiển thị: tên khoản (ghi chú) nếu có, không thì danh mục/loại */
  primary: string;
  /** Dòng phụ: danh mục · ví (hoặc luồng tiền với chuyển khoản, trả thẻ, trả nợ) */
  secondary: string;
  amount: number;
  /** Ví chính của giao dịch (nếu có) */
  walletId?: string;
  /** Sửa/xoá được ở GĐ1 (chỉ thu & chi do người dùng nhập) */
  /** Thu nhập đã đánh dấu là tiền vay giải ngân (không tính vào Thu) */
  loan?: boolean;
  editable: boolean;
  /** Không sửa được nhưng hoàn tác được (chuyển khoản, trả thẻ, trả nợ) */
  reversible?: boolean;
  /** Lý do khoá, hiển thị cho người dùng */
  lockedReason?: string;
  raw: any;
}

type Doc = any;
interface Inputs {
  incomes: Doc[]; expenses: Doc[]; transfers: Doc[]; creditPayments: Doc[]; debtPayments?: Doc[]; sources: Doc[];
}

export const toDate = (v: any): Date | null => {
  if (!v) return null;
  const d = typeof v.toDate === 'function' ? v.toDate() : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Dòng chính = ghi chú nếu có (không trùng danh mục), danh mục xuống dòng phụ cùng ví. */
const names = (category: string, note: string | undefined, wallet: string): { primary: string; secondary: string } => {
  const n = (note ?? '').trim();
  const useNote = n !== '' && n !== category;
  return { primary: useNote ? n : category, secondary: [useNote ? category : '', wallet].filter(Boolean).join(' · ') };
};

export const buildEntries = ({ incomes, expenses, transfers, creditPayments, debtPayments = [], sources }: Inputs): LedgerEntry[] => {
  const wallet = new Map(sources.map(s => [s.id, s.name as string]));
  const wname = (id?: string, fallback?: string) => (id && wallet.get(id)) || fallback || 'Không rõ ví';
  const out: LedgerEntry[] = [];

  for (const r of incomes) {
    const date = toDate(r.transactionDate); if (!date) continue;
    const wid = r.walletId ?? r.sourceId;
    out.push({
      key: `income:${r.id}`, id: r.id, kind: 'income', date,
      title: r.categoryName || r.category || 'Thu nhập',
      subtitle: [wname(wid, r.walletName ?? r.sourceName), r.description || r.notes].filter(Boolean).join(' · '),
      ...names(r.categoryName || r.category || 'Thu nhập', r.description || r.notes, wname(wid, r.walletName ?? r.sourceName)),
      amount: Number(r.amount) || 0, walletId: wid, editable: true, loan: !!r.isLoanProceeds, raw: r
    });
  }
  for (const r of expenses) {
    const date = toDate(r.transactionDate); if (!date) continue;
    const wid = r.walletId ?? r.sourceId;
    const refund = !!r.isRefund;
    const auto = !!r.autoGenerated;
    out.push({
      key: `expense:${r.id}`, id: r.id, kind: refund ? 'refund' : 'expense', date,
      title: r.categoryName || r.category || 'Chi tiêu',
      subtitle: [wname(wid, r.walletName ?? r.sourceName), r.description || r.note].filter(Boolean).join(' · '),
      ...names(r.categoryName || r.category || 'Chi tiêu', r.description || r.note || r.notes, wname(wid, r.walletName ?? r.sourceName)),
      amount: refund ? Math.abs(Number(r.amount) || 0) : -(Number(r.amount) || 0),
      walletId: wid, editable: !auto,
      lockedReason: auto ? 'Khoản này do hệ thống tự ghi (lãi vay / phí chuyển khoản) — sửa ở khoản vay hoặc chuyển khoản gốc.' : undefined,
      raw: r
    });
  }
  const LOCK_P2 = 'Chuyển khoản, trả thẻ và trả nợ không sửa tại chỗ — bấm để hoàn tác rồi nhập lại nếu cần.';
  for (const r of transfers) {
    if (r.status === 'cancelled') continue;
    const date = toDate(r.transferDate); if (!date) continue;
    out.push({
      key: `transfer:${r.id}`, id: r.id, kind: 'transfer', date, title: 'Chuyển khoản',
      subtitle: `${wname(r.fromSourceId)} → ${wname(r.toSourceId)}${r.note ? ` · ${r.note}` : ''}`,
      primary: 'Chuyển khoản', secondary: `${wname(r.fromSourceId)} → ${wname(r.toSourceId)}${r.note ? ` · ${r.note}` : ''}`,
      amount: 0, walletId: r.fromSourceId, editable: false, reversible: true, lockedReason: LOCK_P2, raw: r
    });
  }
  for (const r of creditPayments) {
    const date = toDate(r.date); if (!date) continue;
    out.push({
      key: `card:${r.id}`, id: r.id, kind: 'cardPayment', date, title: 'Trả thẻ tín dụng',
      subtitle: `${wname(r.sourceWalletId, r.sourceWalletName)} → ${r.creditCardName || wname(r.creditCardId)}${r.note ? ` · ${r.note}` : ''}`,
      primary: `Trả ${/^thẻ(\s|$)/i.test(r.creditCardName ?? '') ? r.creditCardName.replace(/^thẻ\s*/i, 'thẻ ') : `thẻ ${r.creditCardName || wname(r.creditCardId)}`}`,
      secondary: [`Từ ${wname(r.sourceWalletId, r.sourceWalletName)}`, r.note].filter(Boolean).join(' · '),
      amount: -(Number(r.amount) || 0), walletId: r.sourceWalletId, editable: false, reversible: true, lockedReason: LOCK_P2, raw: r
    });
  }
  for (const r of debtPayments) {
    const date = toDate(r.paymentDate); if (!date) continue;
    out.push({
      key: `debt:${r.debtId ?? ''}:${r.id}`, id: r.id, kind: 'debtPayment', date, title: 'Trả nợ vay',
      subtitle: `${r.lender || 'Khoản vay'}${r.sourceId ? ` · từ ${wname(r.sourceId)}` : ''}`,
      primary: `Trả nợ ${r.lender || 'khoản vay'}`, secondary: r.sourceId ? `Từ ${wname(r.sourceId)}` : '',
      amount: -(Number(r.totalAmount ?? r.amount) || 0), walletId: r.sourceId ?? undefined, editable: false, reversible: true, lockedReason: LOCK_P2, raw: r
    });
  }
  return out.sort((a, b) => b.date.getTime() - a.date.getTime());
};

export interface EntrySummary { income: number; expense: number; net: number; debtAndCards: number }

/** Thu / Chi (có dấu, hoàn tiền trừ đi) / còn lại / tiền trả thẻ+nợ (không phải chi tiêu). */
export const summarize = (entries: LedgerEntry[]): EntrySummary => {
  let income = 0, expense = 0, debtAndCards = 0;
  for (const e of entries) {
    if (e.kind === 'income') { if (!e.loan) income += e.amount; }
    else if (e.kind === 'expense' || e.kind === 'refund') expense += signedAmount(e.raw);
    else if (e.kind === 'cardPayment' || e.kind === 'debtPayment') debtAndCards += -e.amount;
  }
  return { income, expense, net: income - expense, debtAndCards };
};

export const filterEntries = (
  entries: LedgerEntry[],
  f: { kind: 'all' | 'income' | 'expense' | 'other'; query: string; year: number; month: number | null; quarter?: number | null; walletId?: string; category?: string }
): LedgerEntry[] => {
  const q = f.query.trim().toLowerCase();
  return entries.filter(e => {
    if (e.date.getFullYear() !== f.year) return false;
    if (f.month !== null && e.date.getMonth() + 1 !== f.month) return false;
    if (f.quarter != null && Math.ceil((e.date.getMonth() + 1) / 3) !== f.quarter) return false;
    if (f.kind === 'income' && e.kind !== 'income') return false;
    if (f.kind === 'expense' && e.kind !== 'expense' && e.kind !== 'refund') return false;
    if (f.kind === 'other' && !['transfer', 'cardPayment', 'debtPayment'].includes(e.kind)) return false;
    if (f.walletId && e.walletId !== f.walletId && !(e.kind === 'transfer' && e.raw?.toSourceId === f.walletId)) return false;
    if (f.category && !((e.kind === 'income' || e.kind === 'expense' || e.kind === 'refund') && e.title === f.category)) return false;
    if (q && !`${e.title} ${e.subtitle} ${Math.abs(e.amount)}`.toLowerCase().includes(q)) return false;
    return true;
  });
};
